import React from 'react';
import { LEVEL_META, HOT_LEVELS, LEVEL_ORDER, fmtK, fmtDate, Eyebrow, Title, Panel, Scene, SceneNumber, ProgressBar, LevelBadge, StatusPill, activityColor, btnDark, btnOutline } from './bbShared';
import { LevelCounters } from './LevelControls';

export const SceneDirection = ({ scene, period, onPickLevel }) => (
  <Scene testId="bb-scene">
    <LevelCounters levels={scene.levels} onPick={onPickLevel} testId="bb-scene-level" />
    <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-white/10 sm:divide-x divide-white/10">
    <div className="p-5" data-testid="bb-scene-aide">
      <p className="text-sm sm:text-base font-bold uppercase tracking-[0.08em] text-[#F26A1B]">Besoin d'aide</p>
      <SceneNumber className="mt-2 text-[#F26A1B]">{scene.besoin_aide}</SceneNumber>
      <p className="text-base text-white/85 mt-2">dossiers à débloquer{scene.besoin_aide ? ` · plus ancien ${scene.plus_ancien_aide_jours} j` : ''}</p>
    </div>
    <div className="p-5" data-testid="bb-scene-signes">
      <p className="text-sm sm:text-base font-bold uppercase tracking-[0.08em] text-white/85">Signés · {period === 'month' ? 'mois' : 'semaine'}</p>
      <SceneNumber className="mt-2">{scene.signes_periode}</SceneNumber>
      <p className="text-base text-white/85 mt-2">{period === 'month' ? `${scene.visees_mois} signature(s) visée(s) ce mois` : 'contrat(s) signé(s) cette semaine'}</p>
      <div className="mt-3 flex items-center justify-between text-sm text-white/80"><span>Objectif annuel contrats prospects (au 31/12/{new Date().getFullYear()})</span><b className="text-white">{scene.prospects_annee} / {scene.objectif_annuel}</b></div>
      <div className="mt-1"><ProgressBar value={scene.prospects_annee} max={scene.objectif_annuel} color="#F26A1B" track="rgba(255,255,255,.15)" /></div>
    </div>
    </div>
  </Scene>
);

export const ADebloquer = ({ items, onTake, onOpen }) => (
  <Panel testId="bb-a-debloquer">
    <div className="flex items-baseline justify-between mb-4"><Title className="text-xl">À débloquer</Title><span className="text-xs text-slate-500">Trié par ancienneté du blocage</span></div>
    {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="bb-no-blocked">Aucun dossier bloqué.</p> : (
      <div className="space-y-2.5">
        {items.map(d => (
          <div key={d.id} className="grid grid-cols-1 md:grid-cols-[1.3fr_1.6fr_1fr_auto_auto] gap-3 items-center rounded-xl border border-[#E5E3DE] p-3.5" data-testid={`bb-blocked-${d.id}`}>
            <button type="button" className="text-left" onClick={() => onOpen(d.id)}>
              <p className="font-bold text-sm text-[#1B1C1F]">{d.sim?.compte}</p>
              <p className="text-xs text-slate-500">{d.commercial_name} · {LEVEL_META[d.niveau]?.label} · {fmtK(d.sim?.run_rate)}</p>
            </button>
            <div><Eyebrow>{d.besoin_aide_type}</Eyebrow><p className="text-sm text-[#1B1C1F] mt-0.5">{d.besoin_aide_commentaire}</p></div>
            <div><Eyebrow>Aide attendue</Eyebrow><p className="text-sm font-semibold mt-0.5">{d.besoin_aide_qui}</p></div>
            <p className="text-xl font-bold text-[#C8372D]" style={{ fontFamily: 'var(--font-display)' }}>{d.alerts.aide_depuis_jours ?? 0} j</p>
            {d.pris_en_charge_par
              ? <span className="text-xs font-semibold text-[#1E6B45] px-3" data-testid={`bb-taken-${d.id}`}>Pris par {d.pris_en_charge_par.split(' ')[0]}</span>
              : <button type="button" className={btnOutline} onClick={() => onTake(d.id)} data-testid={`bb-take-${d.id}`}>Je prends le dossier</button>}
          </div>
        ))}
      </div>
    )}
  </Panel>
);

export const AExaminer = ({ sims, onAdd, windowDays }) => {
  if (!sims.length) return null;
  return (
    <Panel testId="bb-a-examiner">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap"><Title className="text-xl">Simulations à examiner en point</Title><span className="text-xs text-slate-500">Simulations des {windowDays} derniers jours, non signées et absentes du Business Commercial</span></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-slate-500 text-left">
            <th className="py-2 pr-3">Simulation</th><th className="pr-3">Commercial</th><th className="pr-3">Créée le</th><th className="pr-3">Avis commercial</th><th className="pr-3 text-right">Run rate</th><th className="pr-3 text-right">CAPEX</th><th className="pr-3 text-right">Payback</th><th />
          </tr></thead>
          <tbody>
            {sims.map(s => (
              <tr key={s.simulation_id} className="border-t border-[#EEECE8]" data-testid={`bb-exam-${s.simulation_id}`}>
                <td className="py-3 pr-3 font-semibold">{s.compte}{s.verdict === 'GO' && <span className="ml-2 text-[10px] font-bold rounded px-1.5 py-0.5 bg-[#DDF1E6] text-[#1E6B45]">GO</span>}</td>
                <td className="pr-3 text-slate-500">{s.commercial_name}</td>
                <td className="pr-3 text-slate-500">{fmtDate(s.created_at)}</td>
                <td className="pr-3"><LevelBadge level={s.temperature} /></td>
                <td className="pr-3 text-right font-bold">{fmtK(s.run_rate)}</td>
                <td className="pr-3 text-right text-slate-500">{fmtK(s.capex)}</td>
                <td className="pr-3 text-right text-slate-500">{s.payback ? `${String(s.payback).replace('.', ',')} mois` : '—'}</td>
                <td className="text-right"><button type="button" className={`${btnDark} py-2`} onClick={() => onAdd(s.simulation_id)} data-testid={`bb-exam-add-${s.simulation_id}`}>Passer en bouillant</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const ParCommercial = ({ rows, params, period }) => (
  <Panel testId="bb-par-commercial">
    <div className="flex items-baseline justify-between mb-3"><Title className="text-xl">Par commercial</Title><span className="text-xs text-slate-500">Simulations en cours par commercial et par température</span></div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[820px]">
        <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-slate-500 text-left">
          <th className="py-2 pr-3">Commercial</th>{LEVEL_ORDER.map(k => <th key={k} className="pr-3">{LEVEL_META[k].label}</th>)}<th className="pr-3">Besoin d'aide</th><th className="pr-3">En cours</th><th className="pr-3">Signés · {period === 'month' ? 'mois' : 'semaine'}</th><th>Objectif {fmtDate(params.date_fin_objectif, true)}</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.commercial_id} className="border-t border-[#EEECE8]" data-testid={`bb-com-${r.commercial_id}`}>
              <td className="py-3 pr-3 font-bold">{r.name}</td>
              {LEVEL_ORDER.map(k => <td key={k} className="pr-3"><b>{r.levels[k].count}</b><span className="text-slate-400"> · {r.levels[k].count ? fmtK(r.levels[k].run_rate) : '—'}</span></td>)}
              <td className={`pr-3 font-bold ${r.besoin_aide ? 'text-[#C8372D]' : 'text-slate-400'}`}>{r.besoin_aide}</td>
              <td className="pr-3">{r.actifs}</td>
              <td className="pr-3 font-bold">{r.signes_periode}</td>
              <td className="min-w-[150px]"><div className="flex items-center gap-2"><ProgressBar value={r.objectif_realise} max={params.objectif_contrats_par_commercial} /><b className="whitespace-nowrap">{r.objectif_realise} / {params.objectif_contrats_par_commercial}</b></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Panel>
);

const MV = { entree: ['Entrée', 'text-slate-500'], montee: ['Montée', 'text-slate-500'], signe: ['Signé', 'text-[#1E8A4C]'], perdu: ['Perdu', 'text-[#C8372D]'], retire: ['Retiré', 'text-slate-500'] };

export const Mouvements = ({ items, period }) => {
  const count = (a) => items.filter(m => m.action === a).length;
  return (
    <Panel testId="bb-mouvements">
      <Title className="text-xl mb-4">{period === 'month' ? 'Mouvements du mois' : 'Depuis le dernier point'}</Title>
      {period === 'month' && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[['entree', 'Entrées'], ['montee', 'Montées'], ['signe', 'Signés'], ['perdu', 'Perdus']].map(([k, l]) => (
            <div key={k} className="rounded-xl bg-[#F3F2EE] p-3 text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>{count(k)}</p><p className="text-[11px] text-slate-500">{l}</p></div>
          ))}
        </div>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-500">Aucun mouvement sur la période.</p> : (
        <ul className="space-y-2.5">
          {items.slice(0, 12).map(m => (
            <li key={m.id} className="grid grid-cols-[70px_1fr] gap-3 text-sm">
              <span className={`text-[10px] font-bold uppercase tracking-[0.12em] pt-0.5 ${MV[m.action]?.[1]}`}>{MV[m.action]?.[0]}</span>
              <span>{m.compte} · {m.commercial_name}{m.action === 'montee' ? ` · ${m.label.replace('Niveau : ', '')}` : ` · ${LEVEL_META[m.niveau]?.label || ''} · ${fmtK(m.run_rate)}`}{m.motif ? ` · motif : ${m.motif}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

export const Activite = ({ rows, onRelancer, relancing, onDetail }) => (
  <Panel testId="bb-activite">
    <div className="flex items-baseline justify-between mb-3 gap-2"><Title className="text-xl">Activité déclarée · semaine écoulée</Title><span className="text-xs text-slate-500">Saisie par les commerciaux · cliquez sur un nom pour sa fiche</span></div>
    <div className="divide-y divide-[#EEECE8]">
      {rows.map(r => (
        <div key={r.commercial_id} className="py-3 grid grid-cols-[1fr_1.2fr_1.2fr] gap-4 items-start" data-testid={`bb-act-${r.commercial_id}`}>
          <button type="button" className="text-left font-bold text-sm underline decoration-dotted underline-offset-4 hover:text-[#C2510F]" onClick={() => onDetail(r.commercial_id)} data-testid={`bb-act-detail-${r.commercial_id}`}>{r.name}</button>
          {[['Contacts', r.contacts, r.objectif_contacts], ['RDV', r.rdv, r.objectif_rdv]].map(([l, v, o]) => (
            <div key={l}><div className="flex justify-between text-[11px] text-slate-500 mb-1"><span>{l}</span><b className="text-[#1B1C1F]">{v} / {o}</b></div><ProgressBar value={v} max={o} color={activityColor(v, o)} /></div>
          ))}
          <div className="col-span-3 flex flex-wrap items-center justify-between gap-2 -mt-1">
            <span className="text-[11px] text-slate-500">Visites prospects <b className="text-[#1B1C1F]">{r.totaux?.nb_visites_prospects ?? 0}</b> · Visites clients <b className="text-[#1B1C1F]">{r.totaux?.nb_visites_clients ?? 0}</b> · Contrats P/R <b className="text-[#1B1C1F]">{r.totaux?.nb_contrats_prospects ?? 0} / {r.totaux?.nb_contrats_renouvellements ?? 0}</b></span>
            <StatusPill status={r.statut} testId={`bb-act-status-${r.commercial_id}`} />
          </div>
        </div>
      ))}
    </div>
    <button type="button" className={`${btnOutline} mt-3`} onClick={onRelancer} disabled={relancing} data-testid="bb-relancer-btn">Relancer les déclarations manquantes</button>
  </Panel>
);

export const LevelLegend = () => (
  <div className="flex flex-wrap gap-2">{LEVEL_ORDER.map(k => <LevelBadge key={k} level={k} />)}</div>
);
